import { createGenerateClassName } from '@material-ui/core/styles';

/*
 * MUI v4 / JSS minifies `makeStyles` class names in production. A
 * `makeStyles(styles, { name: 'BackstageSidebarItem' })` rule that renders as
 * `BackstageSidebarItem-root-123` under the webpack dev server collapses to
 * `jss4-25` once `NODE_ENV` is `production` — see the non-`Mui`, production
 * branch of `@material-ui/styles` `createGenerateClassName`. MUI's own global
 * classes (`MuiButton-root`) are exempt; only named non-MUI sheets are hit.
 *
 * `hone-portal.css` styles the portal by matching those readable names
 * (`[class*='BackstageSidebarItem-root']`, `[class*='BackstageHeader-header']`,
 * `[class*='BackstageSidebarPage-root']`, …). Under the minified names those
 * selectors match nothing, so the sidebar geometry, content gutter, header and
 * tabs all fall back to Backstage's defaults — but only in a production build,
 * which is why `:3000` (dev server) never showed it and `:7007` / the deploy do.
 *
 * This generator forces the readable name in every environment, so production
 * class naming matches the development build the CSS was authored against.
 */
type GenerateId = ReturnType<typeof createGenerateClassName>;

export function createStableClassNameGenerator(): GenerateId {
  /*
   * MUI's own generator handles everything except the broken case: MUI global
   * classes, pseudo-class shorthands and unnamed sheets. A distinct
   * `productionPrefix` keeps its fallback names (`jss5-*`) from ever colliding
   * with Backstage's own `jss4-*` generator installed by `UnifiedThemeProvider`.
   */
  const fallback = createGenerateClassName({ productionPrefix: 'jss5-' });
  let ruleCounter = 0;

  return (rule, styleSheet) => {
    const prefix = styleSheet?.options.classNamePrefix;
    // Named, non-MUI sheets (Backstage's) are the only ones the CSS targets and
    // the only ones MUI minifies. Keep them readable; defer the rest unchanged.
    if (prefix && !prefix.startsWith('Mui')) {
      ruleCounter += 1;
      return `${prefix}-${rule.key}-${ruleCounter}`;
    }
    return fallback(rule, styleSheet);
  };
}
